import { useState } from 'react'
import {
  Plus, Trash2, FileText, Loader, ChevronDown, X, Sparkles, Lightbulb,
  Calendar, Image, Pencil, Clock, AlertTriangle,
} from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'
import { apiPost, apiPut, apiDelete } from '../../lib/auth'

interface CallToAction {
  actionType: string
  url?: string
}

interface Post {
  id: number
  locationId: number
  googleId?: string | null
  type: 'STANDARD' | 'EVENT' | 'OFFER' | string
  summary: string
  imageUrl?: string | null
  callToAction: CallToAction | null
  state: string
  status?: 'draft' | 'scheduled' | 'published' | 'failed'
  scheduledAt?: string | null
  failedReason?: string | null
  content?: string | null
  aiGenerated?: boolean
  createTime: string
  updateTime?: string | null
}

interface SuggestedIdea {
  title: string
  summary: string
}

interface PostsProps {
  locationId: number | null
  projectId: string
}

const POST_TYPES = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'EVENT', label: '\u00C9v\u00E9nement' },
  { value: 'OFFER', label: 'Offre' },
]

const CTA_TYPES = [
  { value: '', label: 'Aucun CTA' },
  { value: 'LEARN_MORE', label: 'En savoir plus' },
  { value: 'CALL', label: 'Appeler' },
  { value: 'BOOK', label: 'R\u00E9server' },
  { value: 'ORDER', label: 'Commander' },
  { value: 'SIGN_UP', label: 'S\'inscrire' },
]

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function postTypeBadge(type: Post['type']) {
  const map: Record<string, { label: string; cls: string }> = {
    STANDARD: { label: 'Standard', cls: 'bg-[#F4F5FA] text-[#8B8FA7]' },
    EVENT: { label: '\u00C9v\u00E9nement', cls: 'bg-amber-400/10 text-amber-400' },
    OFFER: { label: 'Offre', cls: 'bg-score-excellent/10 text-score-excellent' },
  }
  // Type inconnu ou inattendu : ne jamais planter l'onglet, retomber sur un badge neutre.
  const { label, cls } = map[type] ?? { label: type, cls: 'bg-[#F4F5FA] text-[#8B8FA7]' }
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  )
}

function statusBadge(status?: string) {
  if (!status) return null
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Brouillon', cls: 'bg-gray-200/60 text-gray-500' },
    scheduled: { label: 'Programm\u00E9', cls: 'bg-blue-100 text-blue-600' },
    published: { label: 'Publi\u00E9', cls: 'bg-emerald-100 text-emerald-600' },
    failed: { label: '\u00C9chec', cls: 'bg-red-100 text-red-600' },
  }
  const info = map[status]
  if (!info) return null
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${info.cls}`}>
      {info.label}
    </span>
  )
}

export function Posts({ locationId, projectId }: PostsProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingPost, setEditingPost] = useState<Post | null>(null)
  const [postType, setPostType] = useState<Post['type']>('STANDARD')
  const [summary, setSummary] = useState('')
  const [ctaType, setCtaType] = useState('')
  const [ctaUrl, setCtaUrl] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  // AI generation state
  const [generatingContent, setGeneratingContent] = useState(false)
  const [suggestingIdeas, setSuggestingIdeas] = useState(false)
  const [suggestedIdeas, setSuggestedIdeas] = useState<SuggestedIdea[]>([])

  const { data: posts, loading, error, refetch } = useApi<Post[]>(
    locationId ? `/api/local/locations/${locationId}/posts` : ''
  )

  const resetForm = () => {
    setSummary('')
    setCtaType('')
    setCtaUrl('')
    setScheduledAt('')
    setImageFile(null)
    setImagePreview(null)
    setEditingPost(null)
    setPostType('STANDARD')
    setSubmitError(null)
  }

  const openCreateForm = () => {
    resetForm()
    setShowForm(true)
  }

  const openEditForm = (post: Post) => {
    setEditingPost(post)
    setPostType(post.type)
    setSummary(post.summary)
    setCtaType(post.callToAction?.actionType ?? '')
    setCtaUrl(post.callToAction?.url ?? '')
    setScheduledAt(post.scheduledAt ?? '')
    setImageFile(null)
    setImagePreview(null)
    setSubmitError(null)
    setShowForm(true)
  }

  const handleCreate = async () => {
    if (!summary.trim() || !locationId) return
    setSubmitting(true)
    setSubmitError(null)

    if (editingPost) {
      // Edit mode
      const { error: err } = await apiPut(`/api/local/posts/${editingPost.id}`, {
        type: postType,
        summary: summary.trim(),
        callToAction: ctaType ? { actionType: ctaType, url: ctaUrl.trim() || undefined } : undefined,
        status: scheduledAt ? 'scheduled' : undefined,
        scheduledAt: scheduledAt || null,
      })
      setSubmitting(false)
      if (err) {
        setSubmitError(err)
        return
      }
    } else {
      // Create mode
      const { error: err } = await apiPost(`/api/local/locations/${locationId}/posts`, {
        type: postType,
        summary: summary.trim(),
        callToAction: ctaType ? { actionType: ctaType, url: ctaUrl.trim() || undefined } : undefined,
        status: scheduledAt ? 'scheduled' : undefined,
        scheduledAt: scheduledAt || null,
      })
      setSubmitting(false)
      if (err) {
        setSubmitError(err)
        return
      }
    }

    resetForm()
    setShowForm(false)
    refetch()
  }

  const handleDelete = async (postId: number) => {
    setDeletingId(postId)
    await apiDelete(`/api/local/posts/${postId}`)
    setDeletingId(null)
    refetch()
  }

  const handleGenerateContent = async () => {
    if (!locationId) return
    setGeneratingContent(true)
    const { data, error: err } = await apiPost<{ content: string }>('/api/local/posts/generate-content', {
      postType,
      locationId,
    })
    setGeneratingContent(false)
    if (!err && data) {
      setSummary((data as { content: string }).content ?? '')
    }
  }

  const handleSuggestIdeas = async () => {
    if (!locationId) return
    setSuggestingIdeas(true)
    setSuggestedIdeas([])
    const { data, error: err } = await apiPost<{ ideas: SuggestedIdea[] }>('/api/local/posts/suggest-ideas', {
      locationId,
    })
    setSuggestingIdeas(false)
    if (!err && data) {
      setSuggestedIdeas((data as { ideas: SuggestedIdea[] }).ideas ?? [])
    }
  }

  const handlePickIdea = (idea: SuggestedIdea) => {
    setSummary(idea.summary)
    setSuggestedIdeas([])
    if (!showForm) setShowForm(true)
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    const reader = new FileReader()
    reader.onloadend = () => setImagePreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  if (!locationId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-[#8B8FA7] text-sm">S&eacute;lectionnez un &eacute;tablissement pour voir ses posts.</p>
      </div>
    )
  }

  const postList = asArray(posts)
  const isEditing = !!editingPost

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8B8FA7]">
          {posts ? `${postList.length} post${postList.length > 1 ? 's' : ''}` : ''}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSuggestIdeas}
            disabled={suggestingIdeas}
            className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-[#6366F1] bg-[#EEF2FF] rounded-xl hover:bg-[#E0E7FF] transition-colors disabled:opacity-50"
          >
            {suggestingIdeas
              ? <Loader className="w-4 h-4 animate-spin" strokeWidth={1.25} />
              : <Lightbulb className="w-4 h-4" strokeWidth={1.25} />
            }
            Sugg&eacute;rer des sujets
          </button>
          <button
            onClick={openCreateForm}
            className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity"
          >
            <Plus className="w-4 h-4" strokeWidth={1.25} />
            Nouveau post
          </button>
        </div>
      </div>

      {/* Suggested ideas cards */}
      {suggestedIdeas.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[#6366F1] flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5" strokeWidth={1.5} />
            Sujets sugg&eacute;r&eacute;s par l&rsquo;IA
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {suggestedIdeas.map((idea, i) => (
              <button
                key={i}
                onClick={() => handlePickIdea(idea)}
                className="text-left bg-white border border-[#E2E4ED] rounded-xl p-4 hover:border-[#6366F1] hover:shadow-sm transition-all space-y-1.5"
              >
                <p className="text-sm font-medium text-[#1B2559]">{idea.title}</p>
                <p className="text-xs text-[#8B8FA7] line-clamp-3">{idea.summary}</p>
              </button>
            ))}
          </div>
          <button
            onClick={() => setSuggestedIdeas([])}
            className="text-xs text-[#8B8FA7] hover:text-[#1B2559] transition-colors"
          >
            Fermer les suggestions
          </button>
        </div>
      )}

      {/* Create/Edit form */}
      {showForm && (
        <div className="bg-white border border-[#E2E4ED] rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-[#1B2559]">
              {isEditing ? 'Modifier le post' : 'Cr\u00E9er un post GBP'}
            </p>
            <button
              onClick={() => { setShowForm(false); resetForm() }}
              className="text-[#555A73] hover:text-[#1B2559] transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={1.25} />
            </button>
          </div>

          {/* Type select */}
          <div className="space-y-1.5">
            <label className="text-xs text-[#8B8FA7]">Type de post</label>
            <div className="relative">
              <select
                value={postType}
                onChange={e => setPostType(e.target.value as Post['type'])}
                className="w-full appearance-none bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg px-3 py-2 text-sm text-[#1B2559] pr-8 focus:outline-none focus:border-orion-primary/50"
              >
                {POST_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555A73] pointer-events-none" strokeWidth={1.25} />
            </div>
          </div>

          {/* Summary textarea */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs text-[#8B8FA7]">Contenu du post</label>
              <button
                onClick={handleGenerateContent}
                disabled={generatingContent}
                className="flex items-center gap-1 text-xs font-medium text-[#6366F1] hover:text-[#4F46E5] transition-colors disabled:opacity-50"
              >
                {generatingContent
                  ? <Loader className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                  : <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                }
                G&eacute;n&eacute;rer avec l&rsquo;IA
              </button>
            </div>
            <textarea
              value={summary}
              onChange={e => setSummary(e.target.value)}
              placeholder="R&eacute;digez votre post GBP&hellip;"
              rows={4}
              className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg px-3 py-2.5 text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary/50 resize-none"
            />
            <p className="text-xs text-[#555A73] text-right">{summary.length}/1500</p>
          </div>

          {/* Image upload */}
          <div className="space-y-1.5">
            <label className="text-xs text-[#8B8FA7]">Image (optionnel)</label>
            <div className="flex items-start gap-3">
              <label className="flex items-center gap-2 px-3 py-2 bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg text-sm text-[#555A73] cursor-pointer hover:border-[#6366F1] transition-colors">
                <Image className="w-4 h-4" strokeWidth={1.25} />
                {imageFile ? imageFile.name : 'Choisir une image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
              {imagePreview && (
                <div className="relative">
                  <img src={imagePreview} alt="Aper&ccedil;u" className="w-16 h-12 object-cover rounded-lg border border-[#E2E4ED]" />
                  <button
                    onClick={() => { setImageFile(null); setImagePreview(null) }}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#1B2559] text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
              )}
            </div>
            <p className="text-[10px] text-[#8B8FA7]">JPEG/PNG, min 720&times;540, max 5 Mo, ratio 4:3</p>
          </div>

          {/* CTA */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs text-[#8B8FA7]">CTA (optionnel)</label>
              <div className="relative">
                <select
                  value={ctaType}
                  onChange={e => setCtaType(e.target.value)}
                  className="w-full appearance-none bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg px-3 py-2 text-sm text-[#1B2559] pr-8 focus:outline-none focus:border-orion-primary/50"
                >
                  {CTA_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555A73] pointer-events-none" strokeWidth={1.25} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-[#8B8FA7]">URL du CTA</label>
              <input
                type="url"
                value={ctaUrl}
                onChange={e => setCtaUrl(e.target.value)}
                placeholder="https://&hellip;"
                disabled={!ctaType}
                className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg px-3 py-2 text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary/50 disabled:opacity-40"
              />
            </div>
          </div>

          {/* Scheduling */}
          <div className="space-y-1.5">
            <label className="text-xs text-[#8B8FA7] flex items-center gap-1">
              <Calendar className="w-3 h-3" strokeWidth={1.5} />
              Programmer la publication (optionnel)
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
              className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg px-3 py-2 text-sm text-[#1B2559] focus:outline-none focus:border-orion-primary/50"
            />
            {scheduledAt && (
              <p className="text-[10px] text-[#6366F1] flex items-center gap-1">
                <Clock className="w-3 h-3" strokeWidth={1.5} />
                Sera publi&eacute; le {formatDateTime(scheduledAt)}
              </p>
            )}
          </div>

          {submitError && (
            <p className="text-score-critical text-xs">{submitError}</p>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setShowForm(false); resetForm() }}
              className="px-3.5 py-2 text-sm text-[#8B8FA7] hover:text-[#1B2559] transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={submitting || !summary.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting && <Loader className="w-3.5 h-3.5 animate-spin" strokeWidth={1.25} />}
              {isEditing
                ? 'Enregistrer'
                : scheduledAt
                  ? 'Programmer'
                  : 'Publier'
              }
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-score-critical/10 border border-score-critical/20 rounded-xl p-4 text-score-critical text-sm">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && postList.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
            <FileText className="w-6 h-6 text-[#555A73]" strokeWidth={1.25} />
          </div>
          <p className="text-[#1B2559] font-semibold mb-1">Aucun post</p>
          <p className="text-[#8B8FA7] text-sm">Cr&eacute;ez votre premier post GBP pour cet &eacute;tablissement.</p>
        </div>
      )}

      {/* Posts list */}
      {!loading && postList.length > 0 && (
        <div className="space-y-3">
          {postList.map(post => {
            const canEdit = !post.status || post.status === 'draft' || post.status === 'scheduled'
            return (
              <div
                key={post.id}
                className="bg-white border border-[#E2E4ED] rounded-xl p-4 flex items-start justify-between gap-4"
              >
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {postTypeBadge(post.type)}
                    {statusBadge(post.status)}
                    {post.aiGenerated && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#EEF2FF] text-[#6366F1]">
                        <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                        IA
                      </span>
                    )}
                    <span className="text-xs text-[#555A73]">{formatDate(post.createTime)}</span>
                  </div>
                  <p className="text-sm text-[#1B2559] leading-relaxed">{post.summary}</p>

                  {/* Scheduled date */}
                  {post.status === 'scheduled' && post.scheduledAt && (
                    <p className="text-xs text-blue-600 flex items-center gap-1">
                      <Clock className="w-3 h-3" strokeWidth={1.5} />
                      Programm&eacute; le {formatDateTime(post.scheduledAt)}
                    </p>
                  )}

                  {/* Failed reason */}
                  {post.status === 'failed' && post.failedReason && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                      {post.failedReason}
                    </p>
                  )}

                  {post.callToAction?.actionType && (
                    <p className="text-xs text-[#8B8FA7]">
                      CTA : <span className="text-[#818CF8]">{post.callToAction.actionType}</span>
                      {post.callToAction.url && (
                        <> &mdash; <a href={post.callToAction.url} target="_blank" rel="noreferrer" className="underline hover:text-[#1B2559] transition-colors">{post.callToAction.url}</a></>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {canEdit && (
                    <button
                      onClick={() => openEditForm(post)}
                      className="p-1.5 rounded-lg text-[#555A73] hover:text-[#6366F1] hover:bg-[#EEF2FF] transition-all"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.25} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(post.id)}
                    disabled={deletingId === post.id}
                    className="shrink-0 p-1.5 rounded-lg text-[#555A73] hover:text-score-critical hover:bg-score-critical/10 transition-all disabled:opacity-40"
                  >
                    {deletingId === post.id
                      ? <Loader className="w-4 h-4 animate-spin" strokeWidth={1.25} />
                      : <Trash2 className="w-4 h-4" strokeWidth={1.25} />
                    }
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
